import React, { useState, useEffect } from 'react'
import { X, FileSpreadsheet, Loader2, ChevronLeft, ChevronRight } from 'lucide-react'

const API_URL = process.env.REACT_APP_BACKEND_URL

export default function ExcelPreviewModal({ documentId, filename, onClose }) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [data, setData] = useState(null)
  const [activeSheet, setActiveSheet] = useState(0)

  useEffect(() => {
    async function fetchPreview() {
      try {
        setLoading(true)
        const res = await fetch(`${API_URL}/api/documents/${documentId}/preview-excel`)
        if (!res.ok) {
          const err = await res.json().catch(() => ({}))
          throw new Error(err.detail || 'Erreur de chargement')
        }
        const json = await res.json()
        setData(json)
      } catch (e) {
        setError(e.message)
      } finally {
        setLoading(false)
      }
    }
    fetchPreview()
  }, [documentId])

  const sheet = data?.sheets?.[activeSheet]

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
      data-testid="excel-preview-modal"
    >
      <div 
        className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 shrink-0">
          <div className="flex items-center gap-2">
            <FileSpreadsheet size={18} className="text-green-600" />
            <span className="font-semibold text-slate-800 text-sm truncate max-w-md">
              {filename || data?.filename || 'Fichier Excel'}
            </span>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center transition-colors"
            data-testid="close-excel-preview"
          >
            <X size={18} className="text-slate-500" />
          </button>
        </div>

        {/* Sheet tabs */}
        {data?.sheets?.length > 1 && (
          <div className="flex items-center gap-1 px-5 py-2 border-b border-slate-100 bg-slate-50 shrink-0 overflow-x-auto">
            {data.sheets.map((s, i) => (
              <button
                key={i}
                onClick={() => setActiveSheet(i)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors whitespace-nowrap ${
                  i === activeSheet
                    ? 'bg-white text-green-700 border border-green-200 shadow-sm'
                    : 'text-slate-500 hover:bg-slate-100'
                }`}
                data-testid={`sheet-tab-${i}`}
              >
                {s.name}
              </button>
            ))}
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-auto p-0">
          {loading && (
            <div className="flex items-center justify-center py-20">
              <Loader2 size={24} className="animate-spin text-green-600 mr-2" />
              <span className="text-sm text-slate-500">Chargement du fichier...</span>
            </div>
          )}

          {error && (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <p className="text-sm text-red-600 mb-2">{error}</p>
                <button onClick={onClose} className="text-xs text-slate-500 underline">Fermer</button>
              </div>
            </div>
          )}

          {sheet && !loading && (
            <div className="overflow-auto">
              <table className="w-full text-xs border-collapse">
                <thead className="sticky top-0 z-10">
                  <tr className="bg-green-50">
                    <th className="px-2 py-2 border border-green-200 text-green-800 font-bold text-center w-10">#</th>
                    {sheet.headers.map((h, i) => (
                      <th
                        key={i}
                        className="px-3 py-2 border border-green-200 text-green-800 font-bold text-left whitespace-nowrap"
                      >
                        {h || `Col ${i + 1}`}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sheet.rows.map((row, ri) => (
                    <tr key={ri} className={ri % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="px-2 py-1.5 border border-slate-200 text-slate-400 text-center font-mono">
                        {ri + 1}
                      </td>
                      {row.map((cell, ci) => (
                        <td
                          key={ci}
                          className="px-3 py-1.5 border border-slate-200 text-slate-700 whitespace-nowrap max-w-xs truncate"
                          title={cell}
                        >
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer */}
        {sheet && !loading && (
          <div className="flex items-center justify-between px-5 py-2 border-t border-slate-200 bg-slate-50 text-xs text-slate-500 shrink-0">
            <span>
              {sheet.rows.length} ligne(s) affichee(s) sur {sheet.total_rows}
              {sheet.truncated && ' (limite 200 lignes)'}
            </span>
            <span>Feuille: {sheet.name}</span>
          </div>
        )}
      </div>
    </div>
  )
}
